import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';

import InstallmentsEditor from './installmentsEditor';
import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import { toBillingRule } from 'helpers/serviceProviderBilling';
import useTranslate from 'hooks/useTranslate';
import type { BillingRule, BillingRuleFormValues } from 'types';
import { currencyOptions } from 'utils/currencyOptions';
import { billingRuleValidationSchema } from 'utils/validations/serviceProviderBilling';

interface BillingRuleDialogProps {
    title: string;
    subtitle: string;
    description: string;
    initialValues: BillingRuleFormValues;
    loading: boolean;
    onClose: () => void;
    onSubmit: (rule: BillingRule) => void;
}

const BillingRuleDialog = ({
    title,
    subtitle,
    description,
    initialValues,
    loading,
    onClose,
    onSubmit,
}: BillingRuleDialogProps) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const validationSchema = useMemo(() => billingRuleValidationSchema(translate), [translate]);

    return (
        <Dialog
            open
            onClose={loading ? undefined : onClose}
            fullWidth
            maxWidth="md"
            fullScreen={fullScreen}
            scroll="paper"
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => onSubmit(toBillingRule(values))}
            >
                <Box
                    component={Form}
                    noValidate
                    sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                >
                    <DialogTitle>
                        {title}
                        <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                        >
                            {subtitle}
                        </Typography>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2.5}>
                            <Typography variant="body2" color="text.secondary">
                                {description}
                            </Typography>
                            <FormGridLayout>
                                <FormField
                                    type="number"
                                    name="price"
                                    label="price"
                                    required
                                    disabled={loading}
                                />
                                <FormField
                                    type="select"
                                    name="currency"
                                    label="currency"
                                    options={currencyOptions}
                                    required
                                    disabled={loading}
                                />
                            </FormGridLayout>
                            <InstallmentsEditor disabled={loading} />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                            sx={{ textTransform: 'none' }}
                        >
                            {translate('cancel')}
                        </Button>
                        <SubmitButton loading={loading}>save</SubmitButton>
                    </DialogActions>
                </Box>
            </Formik>
        </Dialog>
    );
};

export default BillingRuleDialog;
